// src/pages/EventBadge.js
// Post-registration page: shows the registration status and lets the company
// download a PDF badge (with QR) for each participant. Also used as the payment
// return page for paid events.
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import eventApi, { badgeUrl } from "../config/eventApi";

export default function EventBadge() {
  const { ref } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const location = useLocation();
  // Which of the two routes mounted this page.
  const onStandaloneRoute = /(^|\/)e\/registration\//.test(location.pathname);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoDone, setAutoDone] = useState(false);
  const autoFired = useRef(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await eventApi.verify(ref, null, lang);
        if (active) setData(res.data);
      } catch (e) {
        if (active) setData({ valid: false });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [ref, lang]);

  // When the registration is already confirmed, hand the badges over straight
  // away rather than making the visitor find a button. The manual buttons stay
  // in the table below: a pop-up blocker, a phone that opens PDFs oddly, or
  // someone who simply wants a second copy all need them.
  //
  // Downloads are staggered: browsers silently drop several simultaneous
  // navigations to the same origin, so a company with five participants would
  // otherwise receive one or two badges and never know the rest were lost.
  useEffect(() => {
    if (loading || autoFired.current) return;
    if (!data || !data.valid) return;
    if (data.auto_ready === false) return;              // still pending payment

    const list = (data.participants || []).filter((p) => p.id);
    if (!list.length) return;

    autoFired.current = true;
    list.forEach((p, i) => {
      setTimeout(() => {
        const a = document.createElement("a");
        a.href = badgeUrl(ref, p.id);
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
        if (i === list.length - 1) setAutoDone(true);
      }, 400 + i * 700);
    });
  }, [loading, data, ref]);

  // Match the URL to the event's presentation. Same rule as the registration
  // page: the visitor may arrive here from an e-mail link, a bookmark or the
  // payment gateway's return URL, so the page cannot assume how it was reached.
  useEffect(() => {
    if (loading || !data || !data.valid || data.use_layout === undefined) return;
    const wantsLayout = data.use_layout !== false;
    const prefix = lang === "fr" ? "" : `/${lang}`;
    if (wantsLayout && onStandaloneRoute) {
      navigate(`${prefix}/event/registration/${ref}`, { replace: true });
    } else if (!wantsLayout && !onStandaloneRoute) {
      navigate(`${prefix}/e/registration/${ref}`, { replace: true });
    }
  }, [loading, data, onStandaloneRoute, lang, ref, navigate]);

  if (loading) return null;

  const pending = data && data.statut === "pending";
  const ok = data && data.valid;
  // Standalone events keep their own presentation right through to the
  // confirmation: no site header, no breadcrumb.
  const bare = data && data.use_layout === false;

  return (
    <>
      <Helmet>
        <title>{t("reg.confirmation", "Confirmation d'inscription")} - {t("menu.caci", "CACI")}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <ToastContainer position="top-center" rtl={lang === "ar"} />

      {bare ? (
        /* Standalone presentation: a plain band instead of the site's
           page-title + breadcrumb, which would point back into a website the
           visitor was never shown. */
        <div style={{
          background: "linear-gradient(135deg,#155632 0%,#1c7041 55%,#2f9d63 130%)",
          color: "#fff", padding: "26px 0", boxShadow: "0 4px 18px rgba(28,112,65,.25)",
        }}>
          <div className="container">
            <h1 className="h4 mb-0 fw-bold">
              <i className="bi bi-check2-circle me-2" aria-hidden="true"></i>
              {t("reg.confirmation", "Confirmation d'inscription")}
            </h1>
          </div>
        </div>
      ) : (
        <div className="page-title dark-background">
          <div className="container position-relative">
            <h1>{t("reg.confirmation", "Confirmation d'inscription")}</h1>
            <nav className="breadcrumbs">
              <ol>
                <li><Link to={lang === "fr" ? "/" : `/${lang}`}>{t("menu.home", "Accueil")}</Link></li>
                <li className="current">{t("reg.confirmation", "Confirmation")}</li>
              </ol>
            </nav>
          </div>
        </div>
      )}

      <section className="py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {/* Status banner */}
              {pending ? (
                <div className="alert alert-warning text-center py-4">
                  <i className="bi bi-hourglass-split fs-1 d-block mb-2"></i>
                  <h4>{t("reg.payment_pending", "Paiement en attente")}</h4>
                  <p className="mb-0">
                    {t("reg.payment_pending_note", "Votre inscription sera confirmée après le paiement. Vos badges seront alors disponibles.")}
                  </p>
                  <div className="mt-3">
                    <span className="badge bg-dark fs-6">{t("reg.reference", "Référence")}: {ref}</span>
                  </div>
                </div>
              ) : ok ? (
                <div className="alert alert-success text-center py-4">
                  <i className="bi bi-check-circle-fill fs-1 d-block mb-2"></i>
                  <h4>{t("reg.confirmed", "Inscription confirmée !")}</h4>
                  <p className="mb-0">
                    {autoFired.current
                      ? t("reg.auto_dl_note", "Vos badges se téléchargent automatiquement. Présentez le QR code à l'entrée.")
                      : t("reg.confirmed_note", "Téléchargez les badges ci-dessous. Présentez le QR code à l'entrée.")}
                  </p>

                  {/* The automatic download is invisible by nature: if the
                      browser blocked it, nothing happens and nothing is said.
                      State it plainly and keep a one-click way to retry. */}
                  {autoFired.current && (
                    <div className="mt-3 small">
                      {autoDone ? (
                        <span className="text-success">
                          <i className="bi bi-check2-circle me-1"></i>
                          {t("reg.auto_dl_done", "Téléchargement lancé.")}{" "}
                          {t("reg.auto_dl_blocked", "Rien ne s'est passé ? Votre navigateur a peut-être bloqué la fenêtre :")}
                        </span>
                      ) : (
                        <span className="text-muted">
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {t("reg.auto_dl_running", "Préparation de vos badges…")}
                        </span>
                      )}
                      {autoDone && (
                        <button type="button" className="btn btn-sm btn-outline-success ms-2"
                                onClick={() => {
                                  (data.participants || []).filter((p) => p.id)
                                    .forEach((p, i) => setTimeout(() => {
                                      window.open(badgeUrl(ref, p.id), "_blank", "noopener");
                                    }, i * 700));
                                }}>
                          <i className="bi bi-arrow-repeat me-1"></i>
                          {t("reg.auto_dl_retry", "Relancer le téléchargement")}
                        </button>
                      )}
                    </div>
                  )}

                  <div className="mt-3">
                    <span className="badge bg-dark fs-6">{t("reg.reference", "Référence")}: {ref}</span>
                  </div>
                </div>
              ) : (
                <div className="alert alert-danger text-center py-4">
                  <i className="bi bi-x-octagon-fill fs-1 d-block mb-2"></i>
                  <h4>{t("reg.invalid", "Inscription introuvable")}</h4>
                </div>
              )}

              {/* Summary + badges */}
              {ok && (
                <div className="card shadow-sm">
                  <div className="card-header bg-white">
                    <div className="d-flex justify-content-between">
                      <span><i className="bi bi-calendar-event me-1"></i><strong>{data.event}</strong></span>
                      <span><i className="bi bi-building me-1"></i>{data.company}</span>
                    </div>
                  </div>
                  <div className="card-body">
                    <h6 className="text-muted mb-3">
                      {t("reg.participants_step", "Participants")} ({data.participants?.length || 0})
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-hover align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>{t("reg.participant", "Participant")}</th>
                            <th>{t("reg.badge_no", "N° Badge")}</th>
                            <th className="text-end">{t("reg.badge", "Badge")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data.participants || []).map((p, i) => (
                            <tr key={i}>
                              <td>{p.nom}</td>
                              <td><code>{p.numero_badge}</code></td>
                              <td className="text-end">
                                <BadgeDownload reference={ref} participant={p} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="text-muted small mt-2">
                      <i className="bi bi-qr-code me-1"></i>
                      {t("reg.qr_note", "Chaque badge contient un QR code unique vérifiable à l'entrée.")}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// Per-row badge download. The backend badge endpoint returns a PDF when
// barryvdh/laravel-dompdf is installed, otherwise printable HTML containing the
// QR image (generated via api.qrserver.com). participant.id comes from verify().
function BadgeDownload({ reference, participant }) {
  const { t } = useTranslation();
  // participant.id is included by the backend verify() map when available.
  const href = participant.id ? badgeUrl(reference, participant.id) : null;
  if (!href) {
    return <span className="text-muted small">{t("reg.badge_ready", "Prêt")}</span>;
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
      <i className="bi bi-download me-1"></i>{t("reg.download", "Télécharger")}
    </a>
  );
}